// src/components/drawer/AIControlManager.tsx

import {
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  SmartToy as SmartToyIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useAccount, useErrorHandler, useToast } from "../../../context";
import { AIAuditLog, DisabledConversation } from "../../../interfaces/all";
import { getDisabledConversations, toggleAIState } from "../../../services/api";
import Header from "../../chat/Header/Header";

// Styled components seguindo o padrão dos outros componentes da sidebar
const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
`;

type ConversationCardProps = {
  conversation: DisabledConversation;
  onActivateAI: (phoneNumber: string) => void;
  onDeactivateAI: (phoneNumber: string) => void;
  onShowDetails: (conversation: DisabledConversation) => void;
  activating: string | null;
  isCompanyAIEnabled: boolean;
  formatDate: (date: Date) => string;
  formatRelativeTime: (date: Date) => string;
};

// Componente memoizado para os cards de conversas desabilitadas
const ConversationCard = memo(function ConversationCard({
  conversation,
  onActivateAI,
  onDeactivateAI,
  onShowDetails,
  activating,
  isCompanyAIEnabled,
  formatDate,
  formatRelativeTime,
}: ConversationCardProps) {
  return (
    <Paper
      sx={{
        p: 1.5,
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "1px solid #e9edef",
        borderRadius: "8px",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        "&:hover": {
          backgroundColor: "#f5f6f6",
          borderColor: "#00a884",
          boxShadow: "0 2px 8px rgba(0, 168, 132, 0.1)",
        },
      }}
      onClick={() => onShowDetails(conversation)}
    >
      {/* Header do card */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: conversation.isAIActive ? "#4caf50" : "#ff9800",
              width: 32,
              height: 32,
            }}
          >
            <SmartToyIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "#111b21",
                fontSize: "13px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "200px",
              }}
            >
              {conversation.phoneNumber}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#667781",
                fontSize: "11px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "200px",
              }}
            >
              {conversation.isAIActive ? "IA Ativa" : "IA Desabilitada"}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={conversation.isAIActive ? "Ativa" : "Desabilitada"}
          color={conversation.isAIActive ? "success" : "warning"}
          size="small"
          sx={{ fontSize: "11px", height: "24px" }}
        />
      </Box>

      {/* Informações principais */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Motivo da desabilitação */}
          {conversation.disabledReason && (
            <Typography
              variant="caption"
              sx={{
                color: "#f44336",
                fontSize: "10px",
                fontWeight: 500,
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              {conversation.disabledReason}
            </Typography>
          )}
        </Box>

        {/* Data */}
        <Typography
          variant="caption"
          sx={{
            color: "#667781",
            fontSize: "10px",
            textAlign: "right",
          }}
        >
          {formatDate(conversation.updatedAt)}
        </Typography>
      </Box>

      {/* Footer com informações adicionais */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 1,
          borderTop: "1px solid #f0f2f5",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#667781",
            fontSize: "10px",
          }}
        >
          {formatRelativeTime(conversation.updatedAt)}
        </Typography>

        {/* Botões de ação */}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {conversation.isAIActive ? (
            <Tooltip title="Desativar IA">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeactivateAI(conversation.phoneNumber);
                }}
                disabled={activating === conversation.phoneNumber}
                sx={{
                  color: "#f44336",
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                  width: 24,
                  height: 24,
                  "&:hover": {
                    backgroundColor: "rgba(244, 67, 54, 0.2)",
                  },
                }}
              >
                {activating === conversation.phoneNumber ? (
                  <CircularProgress size={12} />
                ) : (
                  <PauseIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                !isCompanyAIEnabled
                  ? "IA geral da empresa está desativada"
                  : "Ativar IA"
              }
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onActivateAI(conversation.phoneNumber);
                }}
                disabled={
                  activating === conversation.phoneNumber || !isCompanyAIEnabled
                }
                sx={{
                  color: !isCompanyAIEnabled ? "#9e9e9e" : "#4caf50",
                  backgroundColor: !isCompanyAIEnabled
                    ? "rgba(158, 158, 158, 0.1)"
                    : "rgba(76, 175, 80, 0.1)",
                  width: 24,
                  height: 24,
                  "&:hover": {
                    backgroundColor: !isCompanyAIEnabled
                      ? "rgba(158, 158, 158, 0.2)"
                      : "rgba(76, 175, 80, 0.2)",
                  },
                }}
              >
                {activating === conversation.phoneNumber ? (
                  <CircularProgress size={12} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
});

export const AIControlManager: React.FC = () => {
  const { account } = useAccount();
  const { showError } = useToast();
  const { handleError } = useErrorHandler();

  const [conversations, setConversations] = useState<DisabledConversation[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activating, setActivating] = useState<string | null>(null);

  // Estados para filtros e paginação
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedConversation, setSelectedConversation] =
    useState<DisabledConversation | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Verificar se a IA geral da empresa está ativada
  const isCompanyAIEnabled = account?.company?.meta?.aiAgent?.enabled ?? false;

  // Estados para dropdown menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Estados para logs de auditoria
  const [openLogsDialog, setOpenLogsDialog] = useState(false);
  const [logs, setLogs] = useState<AIAuditLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logSearchText, setLogSearchText] = useState("");
  const [logActionFilter, setLogActionFilter] = useState<string>("all");
  const [selectedLog, setSelectedLog] = useState<AIAuditLog | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);
  const logsPerPage = 10;

  // Cleanup adequado para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      // Limpar estados que podem causar vazamentos
      setConversations([]);
      setLogs([]);
      setSelectedConversation(null);
      setSelectedLog(null);
      setAnchorEl(null);
    };
  }, []);

  const loadConversations = useCallback(async () => {
    if (!account?.company_id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getDisabledConversations(1, 50);

      if (response.status === "success" && response.data) {
        const mappedConversations = response.data.conversations.map(
          (conv: any) => ({
            phoneNumber: conv.phoneNumber,
            isAIActive: conv.isAIActive,
            lastHumanInteraction: conv.lastHumanInteraction
              ? new Date(conv.lastHumanInteraction)
              : undefined,
            lastAIInteraction: conv.lastAIInteraction
              ? new Date(conv.lastAIInteraction)
              : undefined,
            disabledReason: conv.disabledReason,
            updatedAt: new Date(conv.updatedAt),
          }),
        );

        setConversations(mappedConversations);
      } else {
        setError("Erro ao carregar conversas desabilitadas");
      }
    } catch (error) {
      handleError(error);
      setError("Erro ao carregar conversas desabilitadas");
    } finally {
      setLoading(false);
    }
  }, [account?.company_id, handleError]);

  // useEffect para carregar conversas
  useEffect(() => {
    if (account?.company_id) {
      loadConversations();
    }
  }, [account?.company_id, loadConversations]);

  const handleActivateAI = useCallback(
    async (phoneNumber: string) => {
      // Se a IA geral da empresa estiver desativada, mostrar mensagem de erro
      if (!isCompanyAIEnabled) {
        showError({
          message:
            "A IA geral da empresa está desativada. Ative primeiro nas configurações da empresa.",
          type: "error",
        });
        return;
      }

      try {
        setActivating(phoneNumber);

        const response = await toggleAIState(phoneNumber, true);

        if (response.status === "success" && response.data) {
          // Atualizar estado local
          setConversations((prev) =>
            prev.map((conv) =>
              conv.phoneNumber === phoneNumber
                ? {
                    ...conv,
                    isAIActive: true,
                    disabledReason: undefined,
                    updatedAt: new Date(),
                  }
                : conv,
            ),
          );
        } else {
          throw new Error("Erro ao reativar IA");
        }
      } catch (error) {
        handleError(error);
      } finally {
        setActivating(null);
      }
    },
    [account?.id, handleError, isCompanyAIEnabled, showError],
  );

  const handleDeactivateAI = useCallback(
    async (phoneNumber: string) => {
      try {
        setActivating(phoneNumber);

        const response = await toggleAIState(
          phoneNumber,
          false,
          "Desativado pelo administrador",
        );

        if (response.status === "success" && response.data) {
          // Atualizar estado local
          setConversations((prev) =>
            prev.map((conv) =>
              conv.phoneNumber === phoneNumber
                ? {
                    ...conv,
                    isAIActive: false,
                    disabledReason: "Desativado pelo administrador",
                    updatedAt: new Date(),
                  }
                : conv,
            ),
          );
        } else {
          throw new Error("Erro ao desativar IA");
        }
      } catch (error) {
        handleError(error);
      } finally {
        setActivating(null);
      }
    },
    [account?.id, handleError],
  );

  const handleShowDetails = useCallback(
    (conversation: DisabledConversation) => {
      setSelectedConversation(conversation);
      setShowDetails(true);
    },
    [],
  );

  const handleCloseDetails = useCallback(() => {
    setShowDetails(false);
    setSelectedConversation(null);
  }, []);

  // Filtrar conversas baseado nos filtros ativos
  const filteredConversations = useMemo(() => {
    if (!Array.isArray(conversations)) {
      return [];
    }

    return conversations.filter((conversation) => {
      // Filtro por texto de busca
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase();
        const phoneNumber = conversation.phoneNumber.toLowerCase();
        const disabledReason = (
          conversation.disabledReason || ""
        ).toLowerCase();

        if (
          !phoneNumber.includes(searchLower) &&
          !disabledReason.includes(searchLower)
        ) {
          return false;
        }
      }

      // Filtro por status
      if (statusFilter !== "all") {
        const isActive = statusFilter === "active";
        if (conversation.isAIActive !== isActive) {
          return false;
        }
      }

      return true;
    });
  }, [conversations, searchText, statusFilter]);

  // Calcular total de páginas
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredConversations.length / 10));
  }, [filteredConversations.length]);

  // Ajustar página atual se necessário
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Funções para paginação
  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  // Funções para dropdown menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleViewLogs = () => {
    setOpenLogsDialog(true);
    loadLogs();
    handleMenuClose();
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      // Simular carregamento de logs do localStorage (como no componente original)
      const storedLogs = localStorage.getItem("ai_audit_logs");
      if (storedLogs) {
        try {
          const parsedLogs = JSON.parse(storedLogs) as AIAuditLog[];
          setLogs(Array.isArray(parsedLogs) ? parsedLogs : []);
        } catch (error) {
          console.error("Erro ao carregar logs de auditoria:", error);
          setLogs([]);
        }
      } else {
        setLogs([]);
      }
      setLogsError(null);
    } catch (error) {
      setLogsError("Erro ao carregar logs de auditoria");
      console.error("Erro ao carregar logs:", error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleShowLogDetails = useCallback((log: AIAuditLog) => {
    setSelectedLog(log);
    setShowLogDetails(true);
  }, []);

  const handleCloseLogDetails = useCallback(() => {
    setShowLogDetails(false);
    setSelectedLog(null);
  }, []);

  // Filtrar logs baseado nos filtros ativos
  const filteredLogs = useMemo(() => {
    if (!Array.isArray(logs)) {
      return [];
    }

    return logs.filter((log) => {
      // Filtro por texto de busca
      if (logSearchText.trim()) {
        const searchLower = logSearchText.toLowerCase();
        if (
          !log.phoneNumber.toLowerCase().includes(searchLower) &&
          !(log.reason || "").toLowerCase().includes(searchLower) &&
          !(log.user?.name || "").toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      // Filtro por ação
      if (logActionFilter !== "all" && log.action !== logActionFilter) {
        return false;
      }

      return true;
    });
  }, [logs, logSearchText, logActionFilter]);

  // Calcular total de páginas para logs
  const logsTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredLogs.length / logsPerPage));
  }, [filteredLogs.length]);

  // Logs paginados para exibição
  const paginatedLogs = useMemo(() => {
    const startIndex = (logsCurrentPage - 1) * logsPerPage;
    const endIndex = startIndex + logsPerPage;
    return filteredLogs.slice(startIndex, endIndex);
  }, [filteredLogs, logsCurrentPage, logsPerPage]);

  // Ajustar página atual dos logs se necessário
  useEffect(() => {
    if (logsCurrentPage > logsTotalPages) {
      setLogsCurrentPage(1);
    }
  }, [logsCurrentPage, logsTotalPages]);

  // Funções para paginação dos logs
  const goToPreviousLogsPage = () => {
    setLogsCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextLogsPage = () => {
    setLogsCurrentPage((prev) => Math.min(logsTotalPages, prev + 1));
  };

  // Funções utilitárias para logs
  const getActionLabel = (action: string) => {
    switch (action) {
      case "activate":
        return "Ativação";
      case "deactivate":
        return "Desativação";
      case "temporary_disable":
        return "Desabilitação Temporária";
      default:
        return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "activate":
        return "success";
      case "deactivate":
        return "error";
      case "temporary_disable":
        return "warning";
      default:
        return "default";
    }
  };

  const formatDate = useCallback((date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  }, []);

  const formatRelativeTime = useCallback((date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return "Agora mesmo";
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`;
    return `${Math.floor(diffInMinutes / 1440)}d atrás`;
  }, []);

  if (loading) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Controle de IA" />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const startIndex = (currentPage - 1) * 10;
  const endIndex = startIndex + 10;
  const currentConversations = filteredConversations.slice(
    startIndex,
    endIndex,
  );

  return (
    <Container>
      {/* Header com botões de ação */}
      <Box sx={{ height: "60px", flexShrink: 0, position: "relative" }}>
        <Header title="Controle de IA" />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: 16,
            transform: "translateY(-50%)",
            display: "flex",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            endIcon={<ExpandMoreIcon />}
            onClick={handleMenuOpen}
            sx={{
              px: 1.5,
              py: 0.5,
              fontSize: "11px",
              borderRadius: "4px",
              backgroundColor: "#00a884",
              textTransform: "none",
              fontWeight: 500,
              minWidth: "auto",
              height: "28px",
              "&:hover": {
                backgroundColor: "#008f6f",
              },
              "& .MuiButton-endIcon": {
                marginLeft: "4px",
                fontSize: "14px",
              },
            }}
          >
            Ações
          </Button>
        </Box>
      </Box>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              border: "1px solid #e9edef",
              minWidth: "160px",
              mt: 0.5,
            },
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={handleViewLogs}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: "13px",
            fontWeight: 500,
            color: "#111b21",
            "&:hover": {
              backgroundColor: "#f0f2f5",
              color: "#00a884",
            },
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <AssessmentIcon sx={{ fontSize: 18, color: "#00a884" }} />
          Ver Logs de Auditoria
        </MenuItem>
      </Menu>

      {/* Conteúdo principal */}
      <ContentContainer>
        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filtros avançados */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f0f2f5",
            borderBottom: "1px solid #e9edef",
            flexShrink: 0,
          }}
        >
          <Stack spacing={2}>
            {/* Campo de busca */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "4px 8px",
                border: "1px solid #e9edef",
                "&:focus-within": {
                  borderColor: "#00a884",
                  boxShadow: "0 0 0 2px rgba(0, 168, 132, 0.1)",
                },
              }}
            >
              <SearchIcon sx={{ color: "#54656f", fontSize: 20, mr: 1 }} />
              <InputBase
                placeholder="Buscar por telefone ou motivo..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#111b21",
                  "&::placeholder": {
                    color: "#667781",
                  },
                }}
              />
            </Box>

            {/* Filtros em linha */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* Filtro por Status */}
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "6px",
                    fontSize: "13px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e9edef",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                  }}
                >
                  <MenuItem value="all">Todos os Status</MenuItem>
                  <MenuItem value="active">IA Ativa</MenuItem>
                  <MenuItem value="disabled">IA Desabilitada</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>

        {/* Cards de conversas - com scroll apenas nos cards */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            p: 2,
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {currentConversations.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                minWidth: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                🤖 Nenhuma conversa desabilitada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {conversations.length === 0
                  ? "Todas as conversas estão com IA ativa"
                  : searchText.trim()
                    ? "Nenhuma conversa encontrada com os filtros aplicados"
                    : "Nenhuma conversa encontrada"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {currentConversations.map((conversation) => (
                <ConversationCard
                  key={conversation.phoneNumber}
                  conversation={conversation}
                  onActivateAI={handleActivateAI}
                  onDeactivateAI={handleDeactivateAI}
                  onShowDetails={handleShowDetails}
                  activating={activating}
                  isCompanyAIEnabled={isCompanyAIEnabled}
                  formatDate={formatDate}
                  formatRelativeTime={formatRelativeTime}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Paginação */}
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 11,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              onClick={goToPreviousPage}
              disabled={loading || currentPage <= 1}
              style={{
                background: currentPage <= 1 || loading ? "#f5f5f5" : "#00a884",
                color: currentPage <= 1 || loading ? "#999" : "white",
                border:
                  currentPage <= 1 || loading ? "1px solid #e0e0e0" : "none",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor: currentPage <= 1 || loading ? "not-allowed" : "pointer",
                fontSize: "12px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                opacity: currentPage <= 1 || loading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {loading && currentPage > 1 ? (
                <>
                  <CircularProgress size={12} sx={{ color: "white" }} />
                  Carregando...
                </>
              ) : (
                "← Anterior"
              )}
            </button>

            <span style={{ fontSize: "12px", color: "#666", padding: "0 8px" }}>
              {currentPage} de {totalPages}
            </span>

            <button
              onClick={goToNextPage}
              disabled={loading || currentPage >= totalPages}
              style={{
                background:
                  currentPage >= totalPages || loading ? "#f5f5f5" : "#00a884",
                color: currentPage >= totalPages || loading ? "#999" : "white",
                border:
                  currentPage >= totalPages || loading
                    ? "1px solid #e0e0e0"
                    : "none",
                borderRadius: "6px",
                padding: "8px 12px",
                cursor:
                  currentPage >= totalPages || loading
                    ? "not-allowed"
                    : "pointer",
                fontSize: "12px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                opacity: currentPage >= totalPages || loading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {loading && currentPage < totalPages ? (
                <>
                  <CircularProgress size={12} sx={{ color: "white" }} />
                  Carregando...
                </>
              ) : (
                "Próximo →"
              )}
            </button>
          </Box>
        </Box>
      </ContentContainer>

      {/* Modal de Detalhes da Conversa */}
      <Dialog
        open={showDetails}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#ffebee",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
              }}
            >
              <SmartToyIcon sx={{ color: "#d32f2f", fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                {selectedConversation?.phoneNumber}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Conversa com IA Desabilitada
              </Typography>
            </Box>
            <Chip
              label="Desabilitada"
              color="error"
              size="small"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                height: "28px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedConversation && (
            <Box>
              {/* Seção de Informações */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  ℹ️ Informações da Conversa
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Telefone
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                        fontFamily: "monospace",
                      }}
                    >
                      {selectedConversation.phoneNumber}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Status da IA
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedConversation.isAIActive
                        ? "Ativa"
                        : "Desabilitada"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Motivo
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedConversation.disabledReason ||
                        "Não especificado"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Última Atualização
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {new Date(selectedConversation.updatedAt).toLocaleString(
                        "pt-BR",
                      )}
                    </Typography>
                  </Box>

                  {selectedConversation.disabledReason && (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#ffebee",
                        borderRadius: "8px",
                        border: "1px solid #ffcdd2",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#c62828",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Motivo da Desabilitação
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#c62828",
                          mt: 0.5,
                        }}
                      >
                        {selectedConversation.disabledReason}
                      </Typography>
                    </Box>
                  )}

                  {selectedConversation.lastHumanInteraction && (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Última Interação Humana
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          mt: 0.5,
                        }}
                      >
                        {formatDate(selectedConversation.lastHumanInteraction)}
                      </Typography>
                    </Box>
                  )}

                  {selectedConversation.lastAIInteraction && (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Última Interação da IA
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          mt: 0.5,
                        }}
                      >
                        {formatDate(selectedConversation.lastAIInteraction)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Seção de Ações */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  ⚙️ Ações
                </Typography>

                <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                  {selectedConversation.isAIActive ? (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<PauseIcon />}
                      onClick={() => {
                        handleDeactivateAI(selectedConversation.phoneNumber);
                        handleCloseDetails();
                      }}
                      disabled={activating === selectedConversation.phoneNumber}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                      }}
                    >
                      {activating === selectedConversation.phoneNumber ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Desativando...
                        </>
                      ) : (
                        "Desativar IA"
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => {
                        handleActivateAI(selectedConversation.phoneNumber);
                        handleCloseDetails();
                      }}
                      disabled={
                        activating === selectedConversation.phoneNumber ||
                        !isCompanyAIEnabled
                      }
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                      }}
                    >
                      {activating === selectedConversation.phoneNumber ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Ativando...
                        </>
                      ) : (
                        "Ativar IA"
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            borderTop: "1px solid #e9edef",
            backgroundColor: "#fff",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Logs de Auditoria */}
      <Dialog
        open={openLogsDialog}
        onClose={() => setOpenLogsDialog(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#e8f5e8",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
              }}
            >
              <AssessmentIcon sx={{ color: "#00a884", fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                Logs de Auditoria da IA
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Histórico de ações de controle da IA
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Filtros para logs */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f0f2f5",
              borderRadius: "8px",
              border: "1px solid #e9edef",
            }}
          >
            <Stack spacing={2}>
              {/* Campo de busca */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  border: "1px solid #e9edef",
                  "&:focus-within": {
                    borderColor: "#00a884",
                    boxShadow: "0 0 0 2px rgba(0, 168, 132, 0.1)",
                  },
                }}
              >
                <SearchIcon sx={{ color: "#54656f", fontSize: 20, mr: 1 }} />
                <InputBase
                  placeholder="Buscar por telefone, usuário ou motivo..."
                  value={logSearchText}
                  onChange={(e) => setLogSearchText(e.target.value)}
                  sx={{
                    flex: 1,
                    fontSize: "14px",
                    color: "#111b21",
                    "&::placeholder": {
                      color: "#667781",
                    },
                  }}
                />
              </Box>

              {/* Filtro por Ação */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={logActionFilter}
                    onChange={(e) => setLogActionFilter(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      fontSize: "13px",
                      width: "100%",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e9edef",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                    }}
                  >
                    <MenuItem value="all">Todas as Ações</MenuItem>
                    <MenuItem value="activate">Ativação</MenuItem>
                    <MenuItem value="deactivate">Desativação</MenuItem>
                    <MenuItem value="temporary_disable">
                      Desabilitação Temporária
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Box>

          {logsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : logsError ? (
            <Alert severity="error">{logsError}</Alert>
          ) : filteredLogs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                📊 Nenhum log encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Os logs de auditoria aparecerão aqui quando as ações de IA forem
                executadas
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {paginatedLogs.map((log: AIAuditLog) => (
                <Paper
                  key={log.id}
                  onClick={() => handleShowLogDetails(log)}
                  sx={{
                    p: 1.5,
                    border: "1px solid #e9edef",
                    borderRadius: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f5f6f6",
                      borderColor: "#00a884",
                      boxShadow: "0 2px 8px rgba(0, 168, 132, 0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          borderRadius: "6px",
                          backgroundColor:
                            log.action === "activate" ? "#e8f5e8" : "#ffebee",
                          color:
                            log.action === "activate" ? "#2e7d32" : "#d32f2f",
                        }}
                      >
                        <SmartToyIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: "#111b21",
                            fontSize: "13px",
                          }}
                        >
                          {log.phoneNumber}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#667781",
                            fontSize: "11px",
                          }}
                        >
                          {log.user?.name || "Usuário não identificado"}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={getActionLabel(log.action)}
                      color={getActionColor(log.action) as any}
                      size="small"
                      sx={{ fontSize: "11px", height: "24px" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pt: 1,
                      borderTop: "1px solid #f0f2f5",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "10px",
                      }}
                    >
                      {log.reason || "Sem motivo especificado"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "10px",
                        textAlign: "right",
                      }}
                    >
                      {formatDate(log.timestamp)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>

        {/* Paginação dos logs */}
        {!logsLoading && !logsError && filteredLogs.length > 0 && (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#fff",
              position: "sticky",
              bottom: 0,
              zIndex: 11,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <button
                onClick={goToPreviousLogsPage}
                disabled={logsLoading || logsCurrentPage <= 1}
                style={{
                  background:
                    logsCurrentPage <= 1 || logsLoading ? "#f5f5f5" : "#00a884",
                  color: logsCurrentPage <= 1 || logsLoading ? "#999" : "white",
                  border:
                    logsCurrentPage <= 1 || logsLoading
                      ? "1px solid #e0e0e0"
                      : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    logsCurrentPage <= 1 || logsLoading
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity: logsCurrentPage <= 1 || logsLoading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {logsLoading && logsCurrentPage > 1 ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "← Anterior"
                )}
              </button>

              <span
                style={{ fontSize: "12px", color: "#666", padding: "0 8px" }}
              >
                {logsCurrentPage} de {logsTotalPages}
              </span>

              <button
                onClick={goToNextLogsPage}
                disabled={logsLoading || logsCurrentPage >= logsTotalPages}
                style={{
                  background:
                    logsCurrentPage >= logsTotalPages || logsLoading
                      ? "#f5f5f5"
                      : "#00a884",
                  color:
                    logsCurrentPage >= logsTotalPages || logsLoading
                      ? "#999"
                      : "white",
                  border:
                    logsCurrentPage >= logsTotalPages || logsLoading
                      ? "1px solid #e0e0e0"
                      : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    logsCurrentPage >= logsTotalPages || logsLoading
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity:
                    logsCurrentPage >= logsTotalPages || logsLoading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {logsLoading && logsCurrentPage < logsTotalPages ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "Próximo →"
                )}
              </button>
            </Box>
          </Box>
        )}

        <DialogActions
          sx={{
            p: 3,
            borderTop: "1px solid #e9edef",
            backgroundColor: "#fff",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => setOpenLogsDialog(false)}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
              },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Detalhes do Log */}
      <Dialog
        open={showLogDetails}
        onClose={handleCloseLogDetails}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            {selectedLog && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor:
                    selectedLog.action === "activate" ? "#e8f5e8" : "#ffebee",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.8)",
                }}
              >
                <SmartToyIcon
                  sx={{
                    color:
                      selectedLog.action === "activate" ? "#2e7d32" : "#d32f2f",
                    fontSize: 24,
                  }}
                />
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                {selectedLog?.phoneNumber}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {selectedLog?.user?.name || "Usuário não identificado"}
              </Typography>
            </Box>
            {selectedLog && (
              <Chip
                label={getActionLabel(selectedLog.action)}
                color={getActionColor(selectedLog.action) as any}
                size="small"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  height: "28px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedLog && (
            <Box>
              {/* Seção de Informações */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  ℹ️ Informações do Log
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Ação Executada
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {getActionLabel(selectedLog.action)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Estado Anterior
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedLog.previousState ? "Ativo" : "Inativo"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Novo Estado
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedLog.newState ? "Ativo" : "Inativo"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Executado em
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {formatDate(selectedLog.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Seção de Motivo */}
              {selectedLog.reason && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#111b21",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📝 Motivo
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#111b21",
                        fontWeight: 500,
                      }}
                    >
                      {selectedLog.reason}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            borderTop: "1px solid #e9edef",
            backgroundColor: "#fff",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleCloseLogDetails}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
              },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AIControlManager;
